import { useCallback, useEffect, useRef, useState } from 'react';

import { useUserId } from '@/auth/UserIdContext';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary';
import {
	getBlobAccessUrl,
	isAzureBlobUrl,
	stripBlobUrlQuery,
} from '@/hooks/useBlobAccessUrl';
import { useImageUpload } from '@/hooks/useImageUpload';
import { logger } from '@/logger';

import { ButtonPopup } from './popups/ButtonPopup.tsx';
import { ColorPopup } from './popups/ColorPopup.tsx';
import { ImagePopup } from './popups/ImagePopup.tsx';
import { PopupPortal } from './popups/PopupPortal.tsx';
import { TextPopup } from './popups/TextPopup.tsx';
import { sanitizeHtml } from './sanitizeHtml.ts';
import type {
	EditableDescriptor,
	EditPatch,
	InlineEmailEditorProps,
} from './types.ts';
import { useEditableElements } from './useEditableElements.ts';
import { useIframeInteraction } from './useIframeInteraction.ts';

const HERO_VISUAL_ID = 'bw-img-herovisual';
const HERO_CTA_ID = 'bw-btn-herocta';
const PRIMARY_CTA_ID = 'bw-btn-primarycta';
const URL_TOKEN_REGEX = /url\(\s*(['"]?)(.*?)\1\s*\)/i;
const URL_TOKEN_GLOBAL_REGEX = /url\(\s*(['"]?)(.*?)\1\s*\)/gi;
const BLOB_URL_ATTRIBUTES = ['src', 'href', 'background'] as const;
const PREVIEW_BLOB_URL_ATTRIBUTES = ['src', 'background'] as const;

function extractFirstUrlToken(value: string): string | null {
	const match = value.match(URL_TOKEN_REGEX);
	if (!match) return null;
	return match[2].trim();
}

function readBackgroundImageUrlFromElement(
	element: HTMLElement,
): string | null {
	const styleAttr = element.getAttribute('style') || '';
	const declarationMatch = styleAttr.match(/background-image\s*:\s*([^;]+)/i);
	if (declarationMatch?.[1]) {
		const inlineUrl = extractFirstUrlToken(declarationMatch[1]);
		if (inlineUrl) return inlineUrl;
	}

	if (element.style.backgroundImage) {
		return extractFirstUrlToken(element.style.backgroundImage);
	}

	return null;
}

function readVisibleButtonText(anchor: HTMLElement): string {
	const spans = Array.from(anchor.querySelectorAll<HTMLElement>('span'));
	for (let i = spans.length - 1; i >= 0; i -= 1) {
		const span = spans[i];
		if (span.children.length === 0 && span.textContent?.trim()) {
			return span.textContent.trim();
		}
	}
	return anchor.textContent?.trim() || '';
}

function readButtonFillColor(anchor: HTMLElement): string | null {
	const candidates = [
		anchor,
		...Array.from(anchor.querySelectorAll<HTMLElement>('span,div,td')),
	];
	for (const candidate of candidates) {
		const styleColor = candidate.style.backgroundColor;
		if (styleColor) return styleColor;
		const bgAttr = candidate.getAttribute('bgcolor');
		if (bgAttr) return bgAttr;
	}
	return null;
}

function escapeAttributeValue(value: string): string {
	return value.replace(/"/g, '&quot;');
}

function escapeHtmlText(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function stripBlobQueryFromValue(value: string): string {
	return isAzureBlobUrl(value) ? stripBlobUrlQuery(value) : value;
}

function stripBlobQueriesFromCssValue(value: string): string {
	URL_TOKEN_GLOBAL_REGEX.lastIndex = 0;
	return value.replace(URL_TOKEN_GLOBAL_REGEX, (match, quote, rawUrl) => {
		const url = rawUrl.trim();
		const strippedUrl = stripBlobQueryFromValue(url);
		if (strippedUrl === url) return match;
		return `url(${quote}${strippedUrl}${quote})`;
	});
}

function stripBlobQueriesFromElementTree(root: ParentNode): void {
	for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
		for (const attribute of BLOB_URL_ATTRIBUTES) {
			const value = el.getAttribute(attribute);
			if (!value) continue;
			const strippedValue = stripBlobQueryFromValue(value);
			if (strippedValue !== value) {
				el.setAttribute(attribute, strippedValue);
			}
		}

		const styleAttr = el.getAttribute('style');
		if (!styleAttr || !styleAttr.toLowerCase().includes('url(')) continue;
		const strippedStyle = stripBlobQueriesFromCssValue(styleAttr);
		if (strippedStyle !== styleAttr) {
			el.setAttribute('style', strippedStyle);
		}
	}
}

async function resolveBlobReferencesForPreview(
	html: string,
	userId: string | null | undefined,
): Promise<string> {
	if (!html || !userId) return html;

	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	const resolutions: Promise<void>[] = [];

	for (const el of Array.from(doc.querySelectorAll<HTMLElement>('*'))) {
		for (const attribute of PREVIEW_BLOB_URL_ATTRIBUTES) {
			const value = el.getAttribute(attribute);
			if (!value || !isAzureBlobUrl(value)) continue;

			resolutions.push(
				getBlobAccessUrl(value, userId)
					.then((accessUrl) => {
						el.setAttribute(attribute, accessUrl);
					})
					.catch(() => {
						// Leave the durable blob URL in place if preview signing fails.
					}),
			);
		}

		const styleAttr = el.getAttribute('style');
		if (!styleAttr || !styleAttr.toLowerCase().includes('url(')) continue;

		URL_TOKEN_GLOBAL_REGEX.lastIndex = 0;
		const urls = Array.from(styleAttr.matchAll(URL_TOKEN_GLOBAL_REGEX))
			.map((match) => match[2].trim())
			.filter(isAzureBlobUrl);
		if (urls.length === 0) continue;

		resolutions.push(
			Promise.all(
				urls.map(async (url) => ({
					url,
					accessUrl: await getBlobAccessUrl(url, userId),
				})),
			)
				.then((resolvedUrls) => {
					let nextStyle = styleAttr;
					for (const { url, accessUrl } of resolvedUrls) {
						nextStyle = nextStyle.replace(url, accessUrl);
					}
					el.setAttribute('style', nextStyle);
				})
				.catch(() => {
					// Keep the sanitized HTML renderable even if one preview URL fails.
				}),
		);
	}

	await Promise.all(resolutions);
	return doc.documentElement.outerHTML;
}

function replaceTagAttributeAt(
	html: string,
	tagStartIndex: number,
	attribute: string,
	value: string,
): string {
	if (tagStartIndex < 0) return html;
	const tagEnd = html.indexOf('>', tagStartIndex);
	if (tagEnd < 0) return html;

	const tag = html.slice(tagStartIndex, tagEnd + 1);
	const safeValue = escapeAttributeValue(value);
	const attrRegex = new RegExp(`\\s${attribute}\\s*=\\s*(['"])(.*?)\\1`, 'i');
	const updatedTag = attrRegex.test(tag)
		? tag.replace(attrRegex, ` ${attribute}="${safeValue}"`)
		: `${tag.slice(0, -1)} ${attribute}="${safeValue}">`;

	return `${html.slice(0, tagStartIndex)}${updatedTag}${html.slice(tagEnd + 1)}`;
}

function replaceLastVisibleLabelInRange(
	html: string,
	rangeStart: number,
	rangeEnd: number,
	label: string,
): string {
	const block = html.slice(rangeStart, rangeEnd);
	const leafSpanRegex = /(<span\b[^>]*>)([^<]*\S[^<]*)(<\/span>)/gi;
	let lastMatch: RegExpExecArray | null = null;
	let candidate = leafSpanRegex.exec(block);
	while (candidate) {
		lastMatch = candidate;
		candidate = leafSpanRegex.exec(block);
	}

	if (lastMatch) {
		const fullMatch = lastMatch[0];
		const startInBlock = lastMatch.index;
		const absoluteStart = rangeStart + startInBlock;
		const replacement = `${lastMatch[1]}${escapeHtmlText(label)}${lastMatch[3]}`;
		return `${html.slice(0, absoluteStart)}${replacement}${html.slice(
			absoluteStart + fullMatch.length,
		)}`;
	}

	// Fallback: some MSO blocks render visible label directly inside <center>.
	const centerRegex = /(<center\b[^>]*>)([\s\S]*?)(<\/center>)/gi;
	let lastCenterMatch: RegExpExecArray | null = null;
	let centerCandidate = centerRegex.exec(block);
	while (centerCandidate) {
		const centerInner = centerCandidate[2];
		const hasNestedMarkup = /<[^>]+>/.test(centerInner);
		if (!hasNestedMarkup && centerInner.trim()) {
			lastCenterMatch = centerCandidate;
		}
		centerCandidate = centerRegex.exec(block);
	}

	if (!lastCenterMatch) return html;
	const centerFullMatch = lastCenterMatch[0];
	const centerStartInBlock = lastCenterMatch.index;
	const centerAbsoluteStart = rangeStart + centerStartInBlock;
	const centerReplacement = `${lastCenterMatch[1]}${escapeHtmlText(label)}${lastCenterMatch[3]}`;
	return `${html.slice(0, centerAbsoluteStart)}${centerReplacement}${html.slice(
		centerAbsoluteStart + centerFullMatch.length,
	)}`;
}

function syncMappedCtaFallback(
	html: string,
	ctaId: string,
	href: string,
	label: string,
	fillColor: string | null,
): string {
	const anchorNeedle = `id="${ctaId}"`;
	const anchorIndex = html.indexOf(anchorNeedle);
	if (anchorIndex < 0) return html;

	const roundRectIndex = html.lastIndexOf('<v:roundrect', anchorIndex);
	if (roundRectIndex < 0) return html;

	let next = replaceTagAttributeAt(html, roundRectIndex, 'href', href || '#');
	if (fillColor) {
		next = replaceTagAttributeAt(next, roundRectIndex, 'fillcolor', fillColor);
	}

	// Recompute anchor position after replacements to avoid stale indices.
	const refreshedAnchorIndex = next.indexOf(anchorNeedle);
	if (refreshedAnchorIndex < 0) return next;

	const blockEnd =
		next.indexOf('</v:roundrect>', refreshedAnchorIndex) +
		'</v:roundrect>'.length;
	if (blockEnd < '</v:roundrect>'.length) return next;

	return replaceLastVisibleLabelInRange(next, roundRectIndex, blockEnd, label);
}

function syncTemplate3MsoFallbacks(
	serializedHtml: string,
	cloneRoot: HTMLElement,
): string {
	const heroVisual = cloneRoot.querySelector<HTMLElement>(`#${HERO_VISUAL_ID}`);
	const heroCta = cloneRoot.querySelector<HTMLElement>(`#${HERO_CTA_ID}`);
	const primaryCta = cloneRoot.querySelector<HTMLElement>(`#${PRIMARY_CTA_ID}`);

	// Only apply VML sync when all template-3 structural anchors are present.
	if (!heroVisual || !heroCta || !primaryCta) return serializedHtml;

	const heroImageUrl = readBackgroundImageUrlFromElement(heroVisual);
	const heroHref = heroCta.getAttribute('href') || '#';
	const heroLabel = readVisibleButtonText(heroCta);
	const heroFillColor = readButtonFillColor(heroCta);

	const primaryHref = primaryCta.getAttribute('href') || '#';
	const primaryLabel = readVisibleButtonText(primaryCta);
	const primaryFillColor = readButtonFillColor(primaryCta);

	let next = serializedHtml;

	if (heroImageUrl) {
		const heroIndex = next.indexOf(`id="${HERO_VISUAL_ID}"`);
		if (heroIndex >= 0) {
			// Template 03 expects <v:fill> after #bw-img-herovisual (forward lookup by design).
			// Sync VML <v:fill src> within the hero <v:rect> block.
			const vRectEnd = next.indexOf('</v:rect>', heroIndex);
			const vFillIndex = next.indexOf('<v:fill', heroIndex);
			if (vFillIndex >= 0 && (vRectEnd < 0 || vFillIndex < vRectEnd)) {
				next = replaceTagAttributeAt(next, vFillIndex, 'src', heroImageUrl);
			}

			// Sync <td background="..."> HTML attribute on the hero element.
			const tdIndex = next.lastIndexOf('<td', heroIndex);
			if (tdIndex >= 0) {
				const tdTag = next.slice(tdIndex, next.indexOf('>', tdIndex) + 1);
				if (tdTag.includes('background=')) {
					next = replaceTagAttributeAt(
						next,
						tdIndex,
						'background',
						heroImageUrl,
					);
				}
			}
		}
	}

	next = syncMappedCtaFallback(
		next,
		HERO_CTA_ID,
		heroHref,
		heroLabel,
		heroFillColor,
	);
	next = syncMappedCtaFallback(
		next,
		PRIMARY_CTA_ID,
		primaryHref,
		primaryLabel,
		primaryFillColor,
	);

	return next;
}

export const InlineEmailEditor = ({
	html,
	brandColors,
	brandImages,
	onHtmlChange,
	onDiagnosticsChange,
}: InlineEmailEditorProps) => {
	const userId = useUserId();
	const imageUpload = useImageUpload(userId || undefined);
	const addImageToLibrary = useAddImageToLibrary(userId || undefined);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [descriptors, setDescriptors] = useState<EditableDescriptor[]>([]);
	const [iframeLoaded, setIframeLoaded] = useState(false);
	const [sanitizedHtml, setSanitizedHtml] = useState('');
	const fallbackObjectUrlsRef = useRef<string[]>([]);
	const isAuthenticated = Boolean(userId && userId.trim());

	const { scan, resetEmittedHtml, lastEmittedHtmlRef } = useEditableElements(
		onHtmlChange,
		onDiagnosticsChange,
	);

	// Reset emitted html ref when input html changes
	const prevHtmlRef = useRef(html);
	useEffect(() => {
		if (html !== prevHtmlRef.current) {
			prevHtmlRef.current = html;
			resetEmittedHtml();
		}
	}, [html, resetEmittedHtml]);

	// Sanitize html
	useEffect(() => {
		let cancelled = false;
		if (html) {
			const nextSanitizedHtml = sanitizeHtml(html);
			resolveBlobReferencesForPreview(nextSanitizedHtml, userId)
				.then((previewHtml) => {
					if (!cancelled) setSanitizedHtml(previewHtml);
				})
				.catch(() => {
					if (!cancelled) setSanitizedHtml(nextSanitizedHtml);
				});
		} else {
			setSanitizedHtml('');
			setDescriptors([]);
		}
		setIframeLoaded(false);
		return () => {
			cancelled = true;
		};
	}, [html, userId]);

	// Clean up fallback object URLs -- for the demo mode
	useEffect(() => {
		return () => {
			for (const objectUrl of fallbackObjectUrlsRef.current) {
				URL.revokeObjectURL(objectUrl);
			}
			fallbackObjectUrlsRef.current = [];
		};
	}, []);

	const handleImageUpload = useCallback(
		async (file: File): Promise<string | null> => {
			if (isAuthenticated) {
				const uploadedUrl = await imageUpload.handleFile(file);
				if (!uploadedUrl) return null;
				// Add the durable blob URL to the library, not the SAS-signed read
				// URL below — the picker needs the stable, re-signable value.
				addImageToLibrary(uploadedUrl, 'email');
				return getBlobAccessUrl(uploadedUrl, userId);
			}

			try {
				const objectUrl = URL.createObjectURL(file);
				fallbackObjectUrlsRef.current.push(objectUrl);
				return objectUrl;
			} catch (error) {
				console.error('Failed to create object URL for image upload:', error);
				return null;
			}
		},
		[addImageToLibrary, imageUpload, isAuthenticated, userId],
	);

	const handleIframeLoad = useCallback(() => {
		const iframeDoc = iframeRef.current?.contentDocument;
		if (!iframeDoc) {
			setIframeLoaded(true);
			return;
		}

		const result = scan(iframeDoc);
		setDescriptors(result.descriptors);
		setIframeLoaded(true);
	}, [scan]);

	const handleSerialize = useCallback(() => {
		const iframeDoc = iframeRef.current?.contentDocument;
		if (!iframeDoc) return;

		// Clone DOM to strip editor artifacts without mutating live document
		const clone = iframeDoc.documentElement.cloneNode(true) as HTMLElement;

		// Strip editor-applied outlines — restore original if captured
		for (const el of Array.from(
			clone.querySelectorAll('[data-bw-editor-outline]'),
		)) {
			const htmlEl = el as HTMLElement;
			const prev = htmlEl.dataset.bwPrevOutline || '';
			htmlEl.style.outline = prev;
			if (htmlEl.getAttribute('style') === '') {
				htmlEl.removeAttribute('style');
			}
			htmlEl.removeAttribute('data-bw-editor-outline');
			htmlEl.removeAttribute('data-bw-prev-outline');
		}

		// Strip editor-applied contenteditable — restore original if captured
		for (const el of Array.from(
			clone.querySelectorAll('[data-bw-editor-ce]'),
		)) {
			const htmlEl = el as HTMLElement;
			const prev = htmlEl.dataset.bwPrevCe;
			if (prev === '__none__') {
				htmlEl.removeAttribute('contenteditable');
			} else {
				htmlEl.setAttribute('contenteditable', prev ?? '');
			}
			htmlEl.removeAttribute('data-bw-editor-ce');
			htmlEl.removeAttribute('data-bw-prev-ce');
		}

		stripBlobQueriesFromElementTree(clone);

		const serialized = clone.outerHTML;
		const syncedSerialized = syncTemplate3MsoFallbacks(serialized, clone);
		if (syncedSerialized !== lastEmittedHtmlRef.current) {
			lastEmittedHtmlRef.current = syncedSerialized;
			onHtmlChange?.(syncedSerialized);
		}
	}, [onHtmlChange, lastEmittedHtmlRef]);

	const { selection, applyEdit, clearSelection, debouncedSerialize } =
		useIframeInteraction({
			iframeRef,
			descriptors,
			onSerialize: handleSerialize,
		});

	const openPanelElementId = selection?.descriptor.id;
	const openPanelRole = selection?.descriptor.role;
	useEffect(() => {
		if (!openPanelElementId) return;
		logger.info('InlineEmailEditor: edit panel opened', {
			elementId: openPanelElementId,
			role: openPanelRole,
		});
		return () => {
			logger.info('InlineEmailEditor: edit panel closed', {
				elementId: openPanelElementId,
				role: openPanelRole,
			});
		};
	}, [openPanelElementId, openPanelRole]);

	const handleApplyEdit = useCallback(
		(descriptor: EditableDescriptor, patch: Partial<EditPatch>) => {
			if (descriptor.role !== 'img' || !patch.src || !isAuthenticated) {
				applyEdit(descriptor, patch);
				return;
			}

			getBlobAccessUrl(patch.src, userId)
				.then((accessUrl) => {
					applyEdit(descriptor, { ...patch, src: accessUrl });
				})
				.catch(() => {
					applyEdit(descriptor, patch);
				});
		},
		[applyEdit, isAuthenticated, userId],
	);

	const iframeDoc = iframeLoaded
		? (iframeRef.current?.contentDocument ?? null)
		: null;

	const renderPopup = () => {
		if (!selection || !iframeDoc) return null;

		const { descriptor, anchorRect, isAnchorVisible, frameRect } = selection;
		const popupProps = { descriptor, iframeDoc, onApplyEdit: handleApplyEdit };

		let content: React.ReactNode;

		// Key by descriptor.id to force remount when switching elements,
		// ensuring popup state (colors, href) resyncs to the new element.
		const key = descriptor.id;

		switch (descriptor.role) {
			case 'heading':
			case 'text':
				content = (
					<TextPopup
						key={key}
						descriptor={descriptor}
						iframeDoc={iframeDoc}
						onRequestSerialize={debouncedSerialize}
						onApplyEdit={handleApplyEdit}
						brandColors={brandColors}
					/>
				);
				break;
			case 'body':
			case 'section':
				content = (
					<ColorPopup key={key} {...popupProps} brandColors={brandColors} />
				);
				break;
			case 'img':
				content = (
					<ImagePopup
						key={key}
						{...popupProps}
						brandImages={brandImages}
						onImageUpload={handleImageUpload}
						isUploading={imageUpload.isUploading}
					/>
				);
				break;
			case 'btn':
				content = (
					<ButtonPopup key={key} {...popupProps} brandColors={brandColors} />
				);
				break;
			default:
				return null;
		}

		return (
			<PopupPortal
				key={descriptor.id}
				anchorRect={anchorRect}
				onDismiss={clearSelection}
				isAnchorVisible={isAnchorVisible}
				frameRect={frameRect}
			>
				{content}
			</PopupPortal>
		);
	};

	return (
		<div className="relative h-full min-h-[400px] w-full">
			<iframe
				ref={iframeRef}
				srcDoc={sanitizedHtml}
				sandbox="allow-same-origin"
				onLoad={handleIframeLoad}
				className="h-full w-full border-0"
				title="Email preview"
			/>
			{renderPopup()}
		</div>
	);
};
